import { AuthManagerBase, ErrorHandlerResult } from "./managerBase";
import { conferenceLocalStorage } from "../tools";
import type { HeimdallSession, SessionProfile, SessionUser } from "@dvrt/graphql-widgets/heimdall";
import { ApolloClient, NextLink, Operation } from "@apollo/client";
import { BackendErrorCode, TStore } from "../types";
import { ExpiredAccessTokenHandler } from "./expiredAccessTokenHandler";
import { SessionEndedError } from "./types";

/** What the browser kept when it ran the OAuth flow itself. The refresh token lives in the backend now. */
const LEGACY_STORAGE_KEYS = ["accessToken", "refreshToken", "beforeLoginUrl"];

/**
 * Keep the profile claims where the rest of the app reads them.
 *
 * They come from Heimdall's `/userinfo`, fetched by the backend at login and handed over with every access token. They
 * are kept in local storage because the locale of the translations is taken from there before anything else is
 * fetched. Written only when they actually change.
 */
function storeProfile(profile: SessionProfile) {
    if (JSON.stringify(conferenceLocalStorage.getSnapshot().profile) !== JSON.stringify(profile))
        conferenceLocalStorage.update({ profile });
}

/**
 * The user logs in to Heimdall through the backend (RFC 10017 section 6.2, token-mediating backend): the refresh token
 * stays there, behind an HttpOnly session cookie, and the session only ever holds a short-lived access token, in
 * memory, which is sent as `Bearer` like before.
 */
export class HeimdallManager extends AuthManagerBase {
    private readonly expiredAccessTokenHandler: ExpiredAccessTokenHandler;
    private readonly session: HeimdallSession;
    private startPromise: Promise<void> | null = null;
    private started = false;
    private _sessionUnavailable = false;

    constructor(apolloClient: ApolloClient<any>, store: TStore, session: HeimdallSession) {
        super(apolloClient, store);
        this.session = session;
        this.expiredAccessTokenHandler = new ExpiredAccessTokenHandler(this);
    }

    handleGraphQLError(code: BackendErrorCode, operation: Operation, forward: NextLink): ErrorHandlerResult {
        switch (code) {
            case "expired_access_token": {
                const retry = this.expiredAccessTokenHandler.renewToken(operation, forward);
                // Without a retry the error has to be reported, the token cannot be renewed again.
                return retry ? { return: retry, handled: true } : {};
            }
            case "unknown_access_token":
                this.restartLogin();
                return { handled: true };
            case "not_authorized":
            case "unreachable":
            case "cant_reach_heimdall":
            case "unknown_invitation":
            case "unknown":
                return {};
            default:
                // the switch is exhaustive, this only guards against a code that is not in the union at runtime
                return {};
        }
    }

    /**
     * Fetches the first access token, if there is a session. Never rejects: when the backend cannot be asked,
     * `sessionUnavailable` tells it apart from having no session at all.
     */
    override start(): Promise<void> {
        return (this.startPromise ??= this.startSession());
    }

    private async startSession(): Promise<void> {
        for (const key of LEGACY_STORAGE_KEYS) window.localStorage.removeItem(key);

        try {
            const user = await this.session.start();
            if (user) storeProfile(user.profile);
        } catch (error) {
            console.warn("could not obtain an access token:", error);
            this._sessionUnavailable = true;
        }

        let accessToken = this.session.currentUser()?.accessToken ?? null;
        this.session.subscribe((user: SessionUser | null) => {
            if (user) storeProfile(user.profile);

            const nextToken = user?.accessToken ?? null;
            if (nextToken === accessToken) return;

            accessToken = nextToken;
            // Both scheduled and reactive renewals change the permissions returned by the Conference API.
            this.rolesInvalidatedEvent.trigger();
        });
        this.started = true;
    }

    override get sessionUnavailable(): boolean {
        return this._sessionUnavailable;
    }

    get isLoggedIn(): boolean {
        return this.session.currentUser() !== null;
    }

    get authHeader(): string {
        return `Bearer ${this.session.currentUser()?.accessToken}`;
    }

    get userName(): string {
        return this.session.currentUser()?.profile.name ?? "";
    }

    override setAuthHeaders(operation: Operation) {
        // The session ended meanwhile (the refresh token expired or was revoked): sending the operation without a token
        // would only end in "not authorized", a new login is what helps.
        if (this.started && !this.isLoggedIn && !this._sessionUnavailable) {
            this.restartLogin();
            return;
        }

        super.setAuthHeaders(operation);
    }

    async login() {
        this.session.redirectToLogin();
    }

    /** Hands the browser to the backend for a new login, which brings the user back to the current page. */
    restartLogin() {
        this.session.redirectToLogin();
    }

    async refreshToken(): Promise<void> {
        console.debug("Renewing the access token");

        const token = await this.session.getAccessToken({ forceRefresh: true });
        if (token === null) throw new SessionEndedError();
    }
}
